/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie, Check, Sliders } from "lucide-react";

const STORAGE_KEY = "webup_cookie_consent";

// Extracted to avoid a nested ternary and make intent explicit.
const getConsentLabel = (category, prefs) => {
  if (category.locked) return "OBLIG.";
  return prefs[category.id] ? "OUI" : "NON";
};

export const CookieBanner = () => {
  const [state, setState] = useState({ open: false, panel: false });
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      const t = setTimeout(() => setState((s) => ({ ...s, open: true })), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const save = (p) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...p, date: new Date().toISOString(), version: 1 })
    );
    setState({ open: false, panel: false });
  };

  const acceptAll = () => save({ essential: true, analytics: true, marketing: true });
  const rejectAll = () => save({ essential: true, analytics: false, marketing: false });
  const savePrefs = () => save(prefs);

  if (!state.open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-desc"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 md:p-5 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-4xl mx-auto bg-white border border-ink/10 shadow-[0_30px_80px_-40px_rgba(11,30,61,0.5)]">
        {!state.panel ? (
          <div className="p-6 md:p-7">
            <div className="flex items-start gap-4">
              <span
                className="h-10 w-10 shrink-0 inline-flex items-center justify-center bg-cyan/10 text-cyan-600"
                aria-hidden="true"
              >
                <Cookie size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <h2 id="cookie-title" className="font-display text-xl md:text-2xl text-ink">
                  Un instant, parlons cookies.
                </h2>
                <p id="cookie-desc" className="mt-2 text-sm text-ink/70 leading-relaxed">
                  Nous utilisons des cookies strictement nécessaires au
                  fonctionnement du site (authentification admin). Vous pouvez
                  également accepter, refuser ou personnaliser les cookies de
                  mesure d'audience. Vos choix sont conservés localement.{" "}
                  <Link to="/cookies" className="link-under text-ink">
                    En savoir plus
                  </Link>
                  .
                </p>
              </div>
              <button
                onClick={rejectAll}
                data-testid="cookie-close"
                className="ml-auto h-9 w-9 inline-flex items-center justify-center text-ink/40 hover:text-ink"
                aria-label="Fermer et refuser les cookies non essentiels"
              >
                <X size={16} />
              </button>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                onClick={acceptAll}
                data-testid="cookie-accept"
                className="inline-flex items-center gap-2 h-11 px-5 btn-premium text-sm font-medium"
                aria-label="Accepter tous les cookies"
              >
                <Check size={14} /> Tout accepter
              </button>
              <button
                onClick={rejectAll}
                data-testid="cookie-reject"
                className="inline-flex items-center gap-2 h-11 px-5 btn-ghost-premium text-sm font-medium"
                aria-label="Refuser les cookies non essentiels"
              >
                Tout refuser
              </button>
              <button
                onClick={() => setState((s) => ({ ...s, panel: true }))}
                data-testid="cookie-personalize"
                className="inline-flex items-center gap-2 h-11 px-5 text-sm text-ink/75 hover:text-ink link-under"
                aria-label="Personnaliser mes préférences cookies"
              >
                <Sliders size={14} /> Personnaliser
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-7">
            <h2 className="font-display text-xl md:text-2xl text-ink">Personnaliser vos cookies</h2>
            <ul className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
              {[
                { id: "essential", title: "Essentiels", desc: "Nécessaires au fonctionnement (authentification admin, consentement).", locked: true },
                { id: "analytics", title: "Mesure d'audience", desc: "Statistiques anonymisées pour améliorer votre expérience." },
                { id: "marketing", title: "Marketing", desc: "Personnalisation et suivi de campagnes." },
              ].map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-4 py-4">
                  <div>
                    <p className="font-medium text-ink">{c.title}</p>
                    <p className="text-sm text-ink/60 mt-0.5">{c.desc}</p>
                  </div>
                  <label className="inline-flex items-center gap-2 shrink-0">
                    <input
                      type="checkbox"
                      disabled={c.locked}
                      checked={c.locked ? true : prefs[c.id]}
                      onChange={(e) => setPrefs((p) => ({ ...p, [c.id]: e.target.checked }))}
                      data-testid={`cookie-toggle-${c.id}`}
                      className="h-4 w-4 accent-cyan-600"
                      aria-label={`Activer les cookies ${c.title}`}
                    />
                    <span className="text-xs font-mono-label text-ink/55">
                      {getConsentLabel(c, prefs)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={savePrefs} data-testid="cookie-save" className="inline-flex items-center gap-2 h-11 px-5 btn-premium text-sm font-medium">
                Enregistrer mes choix
              </button>
              <button onClick={() => setState((s) => ({ ...s, panel: false }))} className="inline-flex items-center gap-2 h-11 px-5 text-sm text-ink/75 hover:text-ink link-under">
                Retour
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
